import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { APP_PATHS } from '@core/constants/route.constant';
import { roleLabel } from '@core/helpers/role-label.helper';
import { utcIso } from '@core/helpers/slot-series.helper';
import { ChannelUsage } from '@core/models';
import { TableColumnConfig } from '@core/models/skeleton.model';
import { TableComponent } from '@custom-components/general/table/table.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

const FEEDS_COLUMNS: TableColumnConfig[] = [
  { field: 'channel', label: 'devices.detailTabs.feeds.channel', width: '24%', bold: true },
  { field: 'installationName', label: 'devices.detailTabs.feeds.installation', width: '24%' },
  { field: 'pointName', label: 'devices.detailTabs.feeds.point', width: '20%' },
  { field: 'role', label: 'devices.detailTabs.feeds.role', width: '16%' },
  { field: 'validFrom', label: 'devices.detailTabs.feeds.validFrom', width: '16%' },
];

// A binding seeded from a device's existing data starts on this date to mean "before anything the
// platform holds". It is not when the device started feeding, so it is shown as a phrase.
const OPEN_START_DATE = '1900-01-01';

@Component({
  selector: 'xemex-device-feeds-tab',
  imports: [TableComponent, TranslateModule],
  providers: [DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './device-feeds-tab.component.html',
})
export class DeviceFeedsTabComponent {
  channelUsage = input<ChannelUsage | null>(null);
  loading = input<boolean>(false);

  private readonly translate = inject(TranslateService);
  private readonly datePipe = inject(DatePipe);

  protected readonly columns = FEEDS_COLUMNS;

  protected readonly rows = computed(
    () =>
      (this.channelUsage()?.usages ?? []).map(usage => {
        return {
          installationOid: usage.installationOid,
          channel: usage.source.label ?? usage.source.valueName ?? '',
          installationName: usage.installationName,
          pointName: usage.pointName,
          role: roleLabel(this.translate, usage.role, usage.role),
          validFrom: this.validFrom(usage.validFrom),
        };
      }) as unknown as Record<string, unknown>[],
  );

  protected readonly hiddenCount = computed(() => this.channelUsage()?.hiddenUsageCount ?? 0);

  protected readonly rowLink = (row: Record<string, unknown>) => [
    '/',
    APP_PATHS.Installations,
    String(row['installationOid']),
    'general',
  ];

  private validFrom(validFrom: string): string {
    if (validFrom.startsWith(OPEN_START_DATE)) {
      return this.translate.instant('devices.detailTabs.feeds.fromTheStart');
    }
    return this.datePipe.transform(utcIso(validFrom), 'medium') ?? '';
  }
}
